"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.SALES_INVOICE_SERIES = void 0;
exports.priceInvoice = priceInvoice;
exports.revenueLines = revenueLines;
exports.serviceInvoiceIntent = serviceInvoiceIntent;
const money_1 = require("../money");
const document_rules_1 = require("../posting/document-rules");
const line_tax_1 = require("../tax/line-tax");
const tax_rule_1 = require("../tax/tax-rule");
/** سلسلة فواتير المبيعات — مستقلة عن سلسلة القيود، والصيغة نفسها `BLOCKED-ACCOUNTING`. */
exports.SALES_INVOICE_SERIES = 'SI';
/**
 * تسعير الفاتورة: صافى كل سطر (كمية × سعر مقربًا لقرشين نصفًا لأعلى)، ثم ضريبته من
 * قاعدته على صافيه، ثم الإجماليات جمعًا لا إعادة حساب — فمجموع السطور يساوى الإجمالى
 * دائمًا بلا فروق تقريب. كل المشاكل تُجمع وتُرجع مرة واحدة بفهرس سطرها.
 */
function priceInvoice(lines, dates) {
    const issues = [];
    if (lines.length === 0) {
        issues.push({ code: 'INVOICE_NO_LINES', message: 'invoice must have at least one line' });
    }
    if (dates.dueDate < dates.issueDate) {
        issues.push({
            code: 'DUE_BEFORE_ISSUE',
            message: 'due date ' + dates.dueDate + ' is before issue date ' + dates.issueDate,
        });
    }
    const priced = [];
    lines.forEach((line, lineIndex) => {
        const quantity = money_1.Money.of(line.quantity);
        const unitPrice = money_1.Money.of(line.unitPrice);
        if (!quantity.isPositive()) {
            issues.push({
                code: 'INVOICE_LINE_QUANTITY',
                message: 'quantity must be positive',
                lineIndex,
            });
        }
        if (unitPrice.isNegative()) {
            issues.push({
                code: 'INVOICE_LINE_PRICE',
                message: 'unit price cannot be negative',
                lineIndex,
            });
        }
        const net = quantity.mul(unitPrice).round(2, 'half_up');
        let tax = null;
        if (line.taxRule) {
            if (!(0, tax_rule_1.isRuleEffective)(line.taxRule, dates.issueDate)) {
                issues.push({
                    code: 'TAX_RULE_NOT_EFFECTIVE',
                    message: 'tax rule ' + line.taxRule.id + ' is not effective on ' + dates.issueDate,
                    lineIndex,
                });
            }
            else {
                tax = (0, tax_rule_1.applyTax)(net, line.taxRule, dates.issueDate);
            }
        }
        priced.push({
            description: line.description,
            quantity,
            unitPrice,
            account: line.account,
            net,
            tax,
        });
    });
    const netTotal = money_1.Money.sum(priced.map((line) => line.net));
    const taxTotal = money_1.Money.sum(priced.map((line) => line.tax?.amount ?? money_1.Money.zero()));
    const total = netTotal.add(taxTotal);
    if (lines.length > 0 && !total.isPositive()) {
        issues.push({ code: 'INVOICE_ZERO_TOTAL', message: 'invoice total must be positive' });
    }
    if (issues.length > 0)
        return { ok: false, issues };
    return { ok: true, value: { lines: priced, netTotal, taxTotal, total } };
}
/**
 * سطور الإيراد مجمّعة بالحساب. السطر الذى لقاعدته ضريبة صفر (صفرى، معفى، خارج النطاق)
 * لا سطر ضريبة له، فيحمل سطر إيراده أثر القاعدة — وإلا غاب وعاؤه عن إقرار القيمة المضافة.
 */
function revenueLines(invoice, accountOf = (line) => line.account) {
    return (0, line_tax_1.groupByAccountAndTax)(invoice.lines.map((line) => ({
        account: accountOf(line),
        amount: line.net,
        tax: line.tax && line.tax.amount.isZero() ? line.tax.snapshot : null,
    })));
}
/**
 * قيد فاتورة الخدمة الآجلة: مدين العملاء بالإجمالى (بمعرّف العميل والفاتورة)، دائن
 * كل حساب إيراد بمجموع صافى سطوره، ودائن ضريبة المخرجات سطرًا لكل سطر خاضع بأثر
 * قاعدته. تجميع الإيراد بالحساب يُبقى القيد قصيرًا ولا يغيّر أى رصيد.
 */
function serviceInvoiceIntent(ctx, input) {
    const { invoice } = input;
    const lines = [
        (0, document_rules_1.debitLine)(ctx.accounts.receivable, invoice.total, ctx.currency, {
            counterparty: { type: 'customer', id: input.customerId, allocationRef: input.invoiceId },
        }),
    ];
    for (const revenue of revenueLines(invoice)) {
        if (revenue.amount.isPositive()) {
            lines.push((0, document_rules_1.creditLine)(revenue.account, revenue.amount, ctx.currency, { tax: revenue.tax }));
        }
    }
    for (const line of invoice.lines) {
        if (line.tax && line.tax.amount.isPositive()) {
            const account = input.outputTaxAccountOf?.({ ruleId: line.tax.snapshot.ruleId }) ?? ctx.accounts.outputTax;
            lines.push((0, document_rules_1.creditLine)(account, line.tax.amount, ctx.currency, { tax: line.tax.snapshot }));
        }
    }
    return {
        companyId: ctx.companyId,
        sourceType: 'sales_invoice',
        sourceId: input.sourceId,
        postingDate: input.postingDate,
        idempotencyKey: input.idempotencyKey,
        reference: input.reference ?? null,
        memo: input.memo ?? null,
        createdBy: ctx.createdBy ?? null,
        lines,
    };
}
//# sourceMappingURL=invoice.js.map